import { Button, Input, InputPassword, Text } from '@kubit-ui-web/react-components';
import { useState, type FormEvent } from 'react';

import { useSocket } from '@/socket/SocketProvider';
import { Card, Row, Stack } from '@/ui/layout';

/**
 * Host sign-in.
 *
 * Only room *creation* is gated, so this is a small panel on the home screen
 * rather than a wall in front of the app. Someone arriving with a room code
 * never sees a reason to touch it.
 */
export function HostLogin() {
  const { isSignedIn, signIn, signOut } = useSocket();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (isSignedIn) {
    return (
      <Card>
        <Row>
          <Text variant="PARAGRAPH_MEDIUM_EXPANDED">Signed in as host.</Text>
          <Button variant="SECONDARY" size="SMALL" onClick={signOut}>
            Sign out
          </Button>
        </Row>
      </Card>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    signIn(username, password)
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <form onSubmit={submit}>
        <Stack>
          <Text variant="HEADING_H3_EXPANDED" component="h2">
            Host sign in
          </Text>
          <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
            Only needed to create a room. Joining one never asks for credentials.
          </Text>

          <Input
            variant="DEFAULT"
            id="host-username"
            label={{ content: 'Username' }}
            value={username}
            autocomplete="username"
            onChange={(event) => setUsername(event.target.value)}
          />
          <InputPassword
            variant="DEFAULT"
            id="host-password"
            label={{ content: 'Password' }}
            type="password"
            value={password}
            autocomplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />

          {error !== null && (
            <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p" color="#c00">
              {error}
            </Text>
          )}

          <Row>
            <Button
              variant="PRIMARY"
              size="MEDIUM"
              type="submit"
              loading={busy}
              disabled={busy || username.trim() === '' || password === ''}
            >
              Sign in
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
